import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { Theme } from '@escala/domain/types';

import { THEME_KEY, applyTheme, isTheme, storeTheme, storedTheme } from './theme';

/** Grava a escolha na conta (no modo API, `PATCH /auth/me/preferences`). */
export type ThemeSaver = (theme: Theme) => Promise<unknown>;

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
  /**
   * Liga o tema à conta de quem entrou: adota o tema salvo nela ou, se a pessoa ainda
   * não escolheu, salva o deste navegador. Daí em diante cada troca vai para a conta.
   * Devolve a função que desliga (ao sair).
   */
  connect: (saved: Theme | null, save: ThemeSaver) => () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Falha ao salvar não desfaz a troca: o tema continua valendo neste navegador. */
function quietly(save: ThemeSaver, theme: Theme) {
  void save(theme).catch(() => undefined);
}

/**
 * Tema claro ou escuro da aplicação inteira, inclusive do login. Guarda a cópia local
 * (`theme.ts`) e, com a sessão aberta, a escolha na conta — a mesma em qualquer
 * computador. Outras abas acompanham a troca pelo evento `storage`.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(storedTheme);
  const current = useRef(theme);
  const saver = useRef<ThemeSaver | null>(null);

  // Antes da pintura: a tela nunca aparece com o tema anterior.
  useLayoutEffect(() => {
    current.current = theme;
    applyTheme(theme);
    storeTheme(theme);
  }, [theme]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_KEY && isTheme(event.newValue)) setThemeState(event.newValue);
    };

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    if (next === current.current) return;

    current.current = next;
    setThemeState(next);
    if (saver.current) quietly(saver.current, next);
  }, []);

  const toggle = useCallback(() => setTheme(current.current === 'dark' ? 'light' : 'dark'), [setTheme]);

  const connect = useCallback((saved: Theme | null, save: ThemeSaver) => {
    saver.current = save;

    if (saved) {
      current.current = saved;
      setThemeState(saved);
    } else {
      quietly(save, current.current);
    }

    return () => {
      if (saver.current === save) saver.current = null;
    };
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggle, connect }), [theme, setTheme, toggle, connect]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);

  if (!value) throw new Error('useTheme precisa estar dentro de <ThemeProvider>');

  return value;
}
